import React, { useState, useEffect } from 'react';
import { 
  Box, TextField, Button, List, ListItem, Typography,
  CircularProgress, Paper, IconButton, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useActivity } from './ActivityContext';
import Cookies from 'js-cookie';

const Message = ({ message, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleEditClick = () => {
    handleClose();
    onEdit(message);
  };

  const handleDeleteClick = () => {
    handleClose();
    onDelete(message);
  };

  return (
    <ListItem
      sx={{
        flexDirection: 'column',
        alignItems: 'flex-start',
        mb: 1,
        bgcolor: '#333',
        borderRadius: 1,
        position: 'relative'
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        width: '100%', 
        justifyContent: 'space-between',
        alignItems: 'flex-start' 
      }}>
        <Box>
          <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
            {message.userName || message.userEmail || 'Unknown'} 
            ({message.senderType})
          </Typography>
          <Typography variant="caption" sx={{ color: '#777', display: 'block' }}>
            {new Date(message.createdAt).toLocaleString()}
          </Typography>
        </Box>
        <IconButton 
          size="small" 
          sx={{ color: '#999' }}
          onClick={handleClick}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <MenuItem onClick={handleEditClick}>Edit</MenuItem>
          <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
        </Menu>
      </Box>
      <Typography sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{message.content}</Typography>
    </ListItem>
  );
};

const MessagesViewer = () => {
  const { selectedActivity } = useActivity();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editMessage, setEditMessage] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState(null);
  const [editContent, setEditContent] = useState('');

  // Fetch messages when selectedActivity changes
  useEffect(() => {
    if (selectedActivity?.guid) {
      fetchMessages();
    }
  }, [selectedActivity]);

  const fetchMessages = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = Cookies.get('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/activity/case-messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          guid: selectedActivity.guid,
          token,
          username: JSON.parse(atob(token.split('.')[1])).sub
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch messages');
      }

      const data = await response.json();
      // Formater meldingene for visning
      const formattedMessages = Array.isArray(data) ? data : [];
      setMessages(formattedMessages);
    } catch (err) {
      setError(err.message || 'Failed to load messages');
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      const token = Cookies.get('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Hent coreValues fra selectedActivity hvis tilgjengelig
      const coreValues = selectedActivity?.coreValues || {
        manufacturerId: selectedActivity?.manufacturerId,
        productTypeId: selectedActivity?.productTypeId,
        serviceTypeId: selectedActivity?.serviceTypeId,
      };

      // Bygg payload for Elkjøp/NGN API
      const payload = {
        coreValues,
        guid: selectedActivity.guid,
        informAboutAccessories: false,
        task: "add_comment",
        content: newMessage.trim(),
        visibleToCustomer: true
      };

      // Bruk Elkjøp/NGN API endpoint hvis det finnes i env, ellers fallback til backend
      const apiUrl = process.env.REACT_APP_NGEN_API_URL || `${process.env.REACT_APP_API_URL}/activity/case-messages/send`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Legg til Authorization header hvis NGN API krever det
          ...(apiUrl.includes('ngen.elkjop.app') && token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }

      setNewMessage('');
      await fetchMessages(); // Refresh messages
    } catch (err) {
      setError(err.message || 'Failed to send message');
      console.error('Error sending message:', err);
    }
  };

  // Ny funksjon for å håndtere redigering
  const handleEdit = async () => {
    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/activity/case-messages/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          guid: selectedActivity.guid,
          messageId: editMessage.id,
          content: editContent,
          token,
          username: JSON.parse(atob(token.split('.')[1])).sub
        })
      });

      if (!response.ok) throw new Error('Failed to update message');
      
      // Oppdater meldingslisten
      await fetchMessages();
      setEditMessage(null);
      setEditContent('');
    } catch (err) {
      console.error('Error updating message:', err);
      setError(err.message);
    }
  };

  // Ny funksjon for å håndtere sletting
  const handleDelete = async () => {
    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/activity/case-messages/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          guid: selectedActivity.guid,
          messageId: deleteMessage.id,
          token,
          username: JSON.parse(atob(token.split('.')[1])).sub
        })
      });

      if (!response.ok) throw new Error('Failed to delete message');
      
      await fetchMessages();
      setDeleteMessage(null);
    } catch (err) {
      console.error('Error deleting message:', err);
      setError(err.message);
    }
  };

  if (!selectedActivity?.guid) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>Select an activity to view messages</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2, p: 2 }}>
      {/* Messages list */}
      <Paper sx={{ 
        flex: 1, 
        overflow: 'auto', 
        bgcolor: '#2a2a2a',
        p: 2
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <List>
            {messages
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .map((message) => (
                <Message 
                  key={message.id}
                  message={message}
                  onEdit={(msg) => {
                    setEditMessage(msg);
                    setEditContent(msg.content);
                  }}
                  onDelete={(msg) => setDeleteMessage(msg)}
                />
            ))}
          </List>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={!!editMessage} onClose={() => setEditMessage(null)}>
        <DialogTitle>Edit Message</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMessage(null)}>Cancel</Button>
          <Button onClick={handleEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteMessage} onClose={() => setDeleteMessage(null)}>
        <DialogTitle>Delete Message</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this message?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteMessage(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Message input */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type your message..."
          variant="outlined"
          size="small"
          multiline
          maxRows={4}
          sx={{
            backgroundColor: '#333',
            '& .MuiOutlinedInput-root': {
              color: 'white',
            }
          }}
        />
        <Button 
          variant="contained" 
          onClick={handleSendMessage}
          disabled={!newMessage.trim()}
        >
          Send
        </Button>
      </Box>
    </Box>
  );
};

export default MessagesViewer;
